import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Document, DocumentProps, pdfjs } from 'react-pdf';
import PDFPage from './PDFPage';
import EclipseLoader from '../../SVG/EclipseLoader';
import { List, AutoSizer } from 'react-virtualized';
import { DocumentCallback, PageCallback } from 'react-pdf/dist/cjs/shared/types';
import { PDFPageProxy } from 'pdfjs-dist';


enum ErrorType{
  load ='PAGE LOAD',
  render = 'PAGE RENDER',
  text = 'GET TEXT',
  strutterTree = 'GET STRUCT TREE',
  annotations = 'GET ANNOTATIONS',
}

type Props = DocumentProps &{
    path: string;
    parentElement?: Element;
    visible?: boolean;
    scale?: number;
    onActionError?: (error: any, message: string) => void;
    onLoadSuccess?: (doc: DocumentCallback) => void;
    onPageRenderSuccess?: (page: PageCallback) => void;
    currentPage?: number;
    onPageInViewPort?: Function;
    showPages?: boolean;
    pageRotateStepsMap?: object;
    // TODO: Implement Highlight feature
    contentToHighlight?: string;
    pageToHighlight?: number;
}
// TODO : Implement the with for web also:
// Page
// const DEFAULT_PAGE_WIDTH = 717;

const options = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
} as const;



const PDFDocument: FunctionComponent<Props> = ({
    path,
    visible = true,
    onActionError,
    currentPage= 1,
    scale = 1,
    onPageInViewPort,
    onPageRenderSuccess,
    onLoadSuccess,
    onLoadProgress,
    parentElement,
    pageRotateStepsMap = {}
}) => {
    const [pageDictionary, setPageDictionary] = useState<Record<number, PDFPageProxy | never>>({});
    const [totalPages, setTotalPages] = useState<number>(0);
    const pagesAspectRations = useRef<Record<number, number | never>>({});
    const listRef = useRef<List>();
    // TODO : Implement the with for web also:
    // const itemWidth = useMemo(
    //   () => {
        //  return (!(rotateSteps % 2) || !page.height) ? DEFAULT_PAGE_WIDTH : page.height,
      // },
      // [
        // pageRotateStepsMap[currentPage],
  //      scale
  //     ]
  // );
      const handleOnCalculateRowHeight =( width: number) => ({index}: { index: number}): number => {
        const  aspectRatio = pagesAspectRations.current[index + 1] || 0

        if(!aspectRatio){
          return 0;
        }

        if(index === totalPages -1){
          return  (width / aspectRatio) + 150;
        }

        return  (width / aspectRatio) + 10 ;
      };


    const scrollToPage =(pageNumber: number) => {
      listRef.current?.scrollToRow(pageNumber);
    };

    useEffect(() => {
      if (currentPage && listRef.current) {
        scrollToPage(currentPage);
      }
    },[currentPage])



    const calculatePageDictionary = async (doc: DocumentCallback) => {
      const pageDictionary = {};
      for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
        const page = await doc.getPage(pageNumber)
        const viewport = page.getViewport({ scale });
        pagesAspectRations.current[pageNumber] = viewport.width / viewport.height;
        pageDictionary[pageNumber] = page;
      }

      setTotalPages(doc.numPages);
      setPageDictionary(pageDictionary);
    }

    const handleLoadSuccess = async (doc: DocumentCallback) => {
        onLoadSuccess?.(doc);
        await calculatePageDictionary(doc);
        setTimeout(() => {
          scrollToPage(currentPage);
        }, 50);
    };

    const handleError = (type: ErrorType) => (error: Error) => {
      onActionError(error, `${type.toString()} ${error.message}`)
    }

    const handleOnScroll = (event: Event) => {
      console.log('event', event);
    }

    const renderLoader = () => (
      <div  className="flex align-center justify-center">
          <EclipseLoader
            color="#17b1e2"
            width="100"
            height="90vh"
            className=" flex align-center justify-center"
          />
      </div>
    );

    const renderItem = (width:number )=>({ index, key ,style }) => {
        const pageNumber = index + 1;
        const page = pageDictionary[pageNumber]

        return (
            <div style={style} key={key}>
               <PDFPage
                  onLoadError={handleError(ErrorType.load)}
                  onRenderError={handleError(ErrorType.render)}
                  onGetTextError={handleError(ErrorType.text)}
                  onGetStructTreeError={handleError(ErrorType.strutterTree)}
                  onGetAnnotationsError={handleError(ErrorType.annotations)}
                  onRenderSuccess={onPageRenderSuccess}
                  scale={scale}
                  width={width}
                  onInViewPortChange={onPageInViewPort}
                  parentElement={parentElement}
                  contentToHighlight={""}
                  rotateSteps={pageRotateStepsMap[pageNumber]}
                  page={page}
                  onScroll={handleOnScroll}
                />
            </div>
        );
    }

    return (
          <AutoSizer >
            {({ width , height }) => {
              return (
                <Document
                    externalLinkTarget="_blank"
                    className={`pdf-viewer__document ${
                        !visible ? 'pdf-viewer__document--hide' : ''
                    }`}
                    file={path}
                    onLoadSuccess={handleLoadSuccess}
                    options={options}
                    onLoadProgress={onLoadProgress}
                    onLoadError={handleError(ErrorType.load)}
                    loading={renderLoader()}
                >
                <List
                  key={width>= height ? 'listLandscape' : 'listPortrait'}
                  width={width}
                  height={height}
                  rowCount={totalPages ?? 0}
                  rowHeight={handleOnCalculateRowHeight(width)}
                  rowRenderer={renderItem(width)}
                  ref={listRef}
                  scrollToIndex={currentPage}
                />
              </Document>
            )}}
            </AutoSizer>
    );
};

export default PDFDocument;
